import { useEffect, useState } from "react";
import cn from "classnames";
import { WorkflowTask } from "./WorkflowTask";
import { refreshApplicationWorkflow, useApplicationWorkflow } from "components/WorkflowTasks/useApplicationWorkflow";
import { WorkflowFilterStepType, WorkFlowTaskModel, WorkflowTaskStepType } from "components/WorkflowTasks/types";
import { Task } from "./Task";
import { AnalyticsEventType, sendAnalyticsEvent } from "components/utils/analytics";
import { isTaskCompleted } from "./utils";

import "./WorkflowTasks.scss";

export const WorkflowTasks = ({
    codeNumber,
    className,
    applicationNumber,
    stepType,
    onAfterTaskComplete,
    onChange,
    onWorkflowLoaded,
}: WorkflowTasksProps) => {
    const [workflow = [], isLoadingWorkflow, , disableFileUploadReq] = useApplicationWorkflow(applicationNumber, stepType, codeNumber);
    const [activeTask, setActiveTask] = useState<WorkFlowTaskModel>();

    useEffect(() => {
        if (!isLoadingWorkflow) {
            onWorkflowLoaded?.();
        }
    }, [isLoadingWorkflow, onWorkflowLoaded]);

    useEffect(() => {
        onChange?.(workflow, disableFileUploadReq);
    }, [disableFileUploadReq, onChange, workflow]);

    const onTaskClick = (task: WorkFlowTaskModel) => {
        setActiveTask(task);
    };

    const onTaskComplete = async (task: WorkFlowTaskModel, codeNumber?: string) => {
        sendAnalyticsEvent(AnalyticsEventType.WORKFLOW_TASK_COMPLETED, { type: task.stepType });

        await refreshApplicationWorkflow(applicationNumber, stepType, codeNumber);
        onAfterTaskComplete?.();
        setActiveTask(undefined);
    };

    if (isLoadingWorkflow || !applicationNumber) {
        return null;
    }

    if (workflow.length === 0) {
        return <div className="mb-3 mt-3"> There are no tasks to complete. </div>;
    }

    return (
        <>
            <div className={cn("workflow-tasks", className)}>
                {workflow
                    .filter((task) => !codeNumber || task.stepType === WorkflowTaskStepType.DocumentUpload)
                    .map((task) => (
                        <WorkflowTask key={task.wfTaskNumber} task={task} onTaskClick={onTaskClick} />
                    ))}
            </div>
            <div className="pb-2 pt-3">{!disableFileUploadReq && "All files are required."}</div>
            {activeTask && applicationNumber && (
                <Task
                    applicationNumber={applicationNumber}
                    task={activeTask}
                    onComplete={onTaskComplete}
                    isTaskCompleted={isTaskCompleted(activeTask)}
                    onClose={() => setActiveTask(undefined)}
                    codeNumber={codeNumber}
                />
            )}
        </>
    );
};

interface WorkflowTasksProps {
    className?: string;
    applicationNumber?: string;
    stepType?: WorkflowFilterStepType;
    onAfterTaskComplete?: () => void;
    onChange?: (workflow: WorkFlowTaskModel[], disableFileUploadReq?: boolean) => void;
    onWorkflowLoaded?: () => void;
    codeNumber?: string;
}
